import type { ActivityKey } from '../activity-journal';
import type { ActivityToastScope } from '../activity-runtime/toast-owner';

const MAX_PERSISTENT_ACTIVITY_TOASTS = 4;

interface RetainedActivityToast {
    readonly id: string;
    readonly scope: ActivityToastScope;
}

export class PersistentActivityToastRegistry {
    private readonly retained = new Map<ActivityKey, RetainedActivityToast>();

    public prepareReplacement(key: ActivityKey): void {
        this.pruneInvalid();
        const replaced = this.retained.get(key);
        if (replaced) replaced.scope.disown(replaced.id);
        this.retained.delete(key);
    }

    public retain(
        key: ActivityKey,
        toast: { readonly id: string; readonly persistent: boolean },
        scope: ActivityToastScope
    ): void {
        if (!toast.persistent) return;

        this.retained.set(key, { id: toast.id, scope });
        if (this.retained.size <= MAX_PERSISTENT_ACTIVITY_TOASTS) return;

        const oldest = this.retained.entries().next().value;
        if (!oldest) return;
        const [oldestKey, retained] = oldest;
        this.retained.delete(oldestKey);
        retained.scope.dismiss(retained.id);
        retained.scope.release();
    }

    public destroyAll(): void {
        for (const retained of this.retained.values()) retained.scope.destroy();
        this.retained.clear();
    }

    public clear(): void {
        this.retained.clear();
    }

    private pruneInvalid(): void {
        for (const [key, retained] of this.retained) {
            if (retained.scope.isCurrent()) continue;
            retained.scope.destroy();
            this.retained.delete(key);
        }
    }
}
